import { motion, AnimatePresence } from "motion/react";
import {
    Wallet,
    ArrowDownLeft,
    ArrowUpRight,
    Copy,
    Check,
    CreditCard,
    Building,
    Bitcoin,
    QrCode,
    Clock,
    AlertCircle,
    X
} from "lucide-react";
import { useState, useMemo } from "react";

import { useToast } from "../components/ui-feedback";
import { useRWAStore, Currency, Transaction } from "../store/rwa-store";

type ModalType = 'deposit' | 'withdraw' | null;
type DepositMethod = 'bank' | 'crypto';
type FilterType = 'all' | 'deposit' | 'withdrawal' | 'invest' | 'distribution';

export default function WalletView() {
    const { currentUser, transactions, deposit, withdraw } = useRWAStore();
    const { showToast } = useToast();
    const [modal, setModal] = useState<ModalType>(null);
    const [selectedCurrency, setSelectedCurrency] = useState<Currency>('USD');
    const [depositMethod, setDepositMethod] = useState<DepositMethod>('bank');
    const [amount, setAmount] = useState("");
    const [processing, setProcessing] = useState(false);
    const [success, setSuccess] = useState(false);
    const [copied, setCopied] = useState(false);
    const [txFilter, setTxFilter] = useState<FilterType>('all');

    // Currency configs
    const currencies: { id: Currency; name: string; icon: React.ComponentType<{ className?: string }>; color: string }[] = [
        { id: 'USD', name: 'US Dollar', icon: CreditCard, color: 'from-green-500 to-emerald-600' },
        { id: 'USDC', name: 'USD Coin', icon: Bitcoin, color: 'from-blue-500 to-cyan-500' },
        { id: 'EUR', name: 'Euro', icon: Building, color: 'from-purple-500 to-indigo-500' },
    ];

    // User transactions
    const userTransactions = useMemo(() => {
        if (!currentUser) return [];
        return transactions
            .filter(tx => tx.userId === currentUser.id || tx.userId === 'system')
            .filter(tx => txFilter === 'all' ? true : tx.type === txFilter)
            .slice(0, 20);
    }, [transactions, currentUser, txFilter]);

    const handleCopy = (text: string) => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleDeposit = () => {
        if (!amount || parseFloat(amount) <= 0) return;
        setProcessing(true);

        setTimeout(() => {
            const numAmount = parseFloat(amount);
            deposit(numAmount, selectedCurrency);
            setProcessing(false);
            setSuccess(true);
            showToast('success', 'Deposit Successful', `$${numAmount.toLocaleString()} ${selectedCurrency} has been added to your wallet.`);
            setTimeout(() => {
                setSuccess(false);
                setModal(null);
                setAmount("");
            }, 2000);
        }, 1500);
    };

    const handleWithdraw = () => {
        const numAmount = parseFloat(amount);
        if (!amount || numAmount <= 0) return;
        if (!currentUser?.wallet || currentUser.wallet.balances[selectedCurrency] < numAmount) return;

        setProcessing(true);

        setTimeout(() => {
            withdraw(numAmount, selectedCurrency);
            setProcessing(false);
            setSuccess(true);
            showToast('success', 'Withdrawal Initiated', `$${numAmount.toLocaleString()} ${selectedCurrency} withdrawal is being processed.`);
            setTimeout(() => {
                setSuccess(false);
                setModal(null);
                setAmount("");
            }, 2000);
        }, 1500);
    };

    if (!currentUser?.wallet) return null;

    const wallet = currentUser.wallet;
    const totalBalance = Object.values(wallet.balances).reduce((a, b) => a + b, 0);

    return (
        <div className="max-w-6xl mx-auto space-y-8">
            {/* HEADER */}
            <header className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight mb-2">Wallet</h1>
                    <p className="text-neutral-400">Manage your funds and transactions</p>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={() => setModal('deposit')}
                        className="flex items-center gap-2 px-5 py-2.5 bg-emerald-500 text-black rounded-xl text-sm font-bold hover:bg-emerald-400 transition-colors"
                    >
                        <ArrowDownLeft className="w-4 h-4" /> Deposit
                    </button>
                    <button
                        onClick={() => setModal('withdraw')}
                        className="flex items-center gap-2 px-5 py-2.5 bg-neutral-900 border border-neutral-800 text-white rounded-xl text-sm font-bold hover:bg-neutral-800 transition-colors"
                    >
                        <ArrowUpRight className="w-4 h-4" /> Withdraw
                    </button>
                </div>
            </header>

            {/* WALLET ADDRESS */}
            <div className="bg-neutral-900/40 border border-neutral-800 rounded-2xl p-6">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-linear-to-br from-emerald-500 to-cyan-500 rounded-xl flex items-center justify-center">
                            <Wallet className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <div className="text-sm text-neutral-500 mb-1">Custodial Wallet Address</div>
                            <div className="font-mono text-lg">{wallet.address}</div>
                        </div>
                    </div>
                    <button
                        onClick={() => handleCopy(wallet.address)}
                        className="flex items-center gap-2 px-4 py-2 bg-neutral-800 rounded-lg text-sm hover:bg-neutral-700 transition-colors"
                    >
                        {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                        {copied ? 'Copied!' : 'Copy'}
                    </button>
                </div>
            </div>

            {/* BALANCE CARDS */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {currencies.map(currency => {
                    const balance = wallet.balances[currency.id] || 0;
                    const pending = wallet.pending?.[currency.id] || 0;
                    const Icon = currency.icon;

                    return (
                        <motion.div
                            key={currency.id}
                            whileHover={{ scale: 1.02 }}
                            className="relative overflow-hidden bg-neutral-900/40 border border-neutral-800 rounded-2xl p-6 hover:border-neutral-700 transition-all cursor-pointer group"
                            onClick={() => {
                                setSelectedCurrency(currency.id);
                                setModal('deposit');
                            }}
                        >
                            {/* Gradient Background */}
                            <div className={`absolute top-0 right-0 w-32 h-32 bg-linear-to-br ${currency.color} opacity-10 blur-3xl group-hover:opacity-20 transition-opacity`} />

                            <div className="relative z-10">
                                <div className="flex justify-between items-start mb-6">
                                    <div className={`w-10 h-10 rounded-xl bg-linear-to-br ${currency.color} flex items-center justify-center shadow-lg`}>
                                        <Icon className="w-5 h-5 text-white" />
                                    </div>
                                    <span className="text-xs font-mono text-neutral-500 uppercase">{currency.id}</span>
                                </div>

                                <div className="text-3xl font-bold font-mono mb-1">
                                    ${balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </div>
                                <div className="text-sm text-neutral-500">{currency.name}</div>

                                {pending > 0 && (
                                    <div className="mt-4 flex items-center gap-2 text-xs text-amber-500">
                                        <Clock className="w-3 h-3" />
                                        <span>${pending.toLocaleString()} pending</span>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    );
                })}
            </div>

            {/* TOTAL SUMMARY */}
            <div className="bg-linear-to-r from-neutral-900 to-neutral-900/50 border border-neutral-800 rounded-2xl p-6">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div>
                        <div className="text-sm text-neutral-500 mb-2">Total Balance (All Currencies)</div>
                        <div className="text-4xl font-bold font-mono">
                            ${totalBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                    </div>
                    <div className="flex gap-6">
                        <div className="text-center">
                            <div className="text-2xl font-bold font-mono text-emerald-500">
                                ${userTransactions.filter(tx => tx.type === 'deposit').reduce((a, b) => a + b.amount, 0).toLocaleString()}
                            </div>
                            <div className="text-xs text-neutral-500">Total Deposited</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold font-mono text-blue-500">
                                ${userTransactions.filter(tx => tx.type === 'invest').reduce((a, b) => a + b.amount, 0).toLocaleString()}
                            </div>
                            <div className="text-xs text-neutral-500">Total Invested</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* TRANSACTION HISTORY */}
            <div className="bg-neutral-900/40 border border-neutral-800 rounded-2xl overflow-hidden">
                <div className="p-6 border-b border-neutral-800 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <h3 className="text-lg font-bold">Transaction History</h3>
                    <div className="flex gap-2">
                        {(['all', 'deposit', 'withdrawal', 'invest', 'distribution'] as FilterType[]).map(filter => (
                            <button
                                key={filter}
                                onClick={() => setTxFilter(filter)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors ${txFilter === filter
                                    ? 'bg-white text-black'
                                    : 'bg-neutral-800 text-neutral-400 hover:bg-neutral-700'
                                    }`}
                            >
                                {filter}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="divide-y divide-neutral-800/50">
                    {userTransactions.length > 0 ? userTransactions.map(tx => (
                        <TransactionRow key={tx.id} transaction={tx} />
                    )) : (
                        <div className="p-12 text-center text-neutral-500">
                            No transactions found
                        </div>
                    )}
                </div>
            </div>

            {/* DEPOSIT MODAL */}
            <AnimatePresence>
                {modal === 'deposit' && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-xs p-4">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="bg-neutral-900 border border-neutral-800 rounded-2xl w-full max-w-lg overflow-hidden"
                        >
                            <div className="p-6 border-b border-neutral-800 flex justify-between items-center">
                                <h2 className="text-xl font-bold">Deposit Funds</h2>
                                <button onClick={() => setModal(null)} className="p-2 hover:bg-neutral-800 rounded-lg">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            {success ? (
                                <div className="p-12 text-center">
                                    <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <Check className="w-8 h-8 text-emerald-500" />
                                    </div>
                                    <h3 className="text-xl font-bold mb-2">Deposit Successful!</h3>
                                    <p className="text-neutral-400">Your funds have been added to your wallet</p>
                                </div>
                            ) : (
                                <div className="p-6 space-y-6">
                                    {/* Currency Selection */}
                                    <div>
                                        <label className="block text-sm font-medium text-neutral-400 mb-3">Select Currency</label>
                                        <div className="grid grid-cols-3 gap-2">
                                            {currencies.map(c => (
                                                <button
                                                    key={c.id}
                                                    onClick={() => setSelectedCurrency(c.id)}
                                                    className={`p-3 rounded-xl border text-center transition-all ${selectedCurrency === c.id
                                                        ? 'bg-white/10 border-white/20 text-white'
                                                        : 'border-neutral-800 text-neutral-500 hover:border-neutral-700'
                                                        }`}
                                                >
                                                    <div className="font-mono font-bold">{c.id}</div>
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Deposit Method */}
                                    <div>
                                        <label className="block text-sm font-medium text-neutral-400 mb-3">Deposit Method</label>
                                        <div className="grid grid-cols-2 gap-2">
                                            <button
                                                onClick={() => setDepositMethod('bank')}
                                                className={`p-4 rounded-xl border flex items-center gap-3 transition-all ${depositMethod === 'bank'
                                                    ? 'bg-white/10 border-white/20'
                                                    : 'border-neutral-800 hover:border-neutral-700'
                                                    }`}
                                            >
                                                <Building className="w-5 h-5" />
                                                <div className="text-left">
                                                    <div className="font-medium">Bank Transfer</div>
                                                    <div className="text-xs text-neutral-500">1-2 business days</div>
                                                </div>
                                            </button>
                                            <button
                                                onClick={() => setDepositMethod('crypto')}
                                                className={`p-4 rounded-xl border flex items-center gap-3 transition-all ${depositMethod === 'crypto'
                                                    ? 'bg-white/10 border-white/20'
                                                    : 'border-neutral-800 hover:border-neutral-700'
                                                    }`}
                                            >
                                                <Bitcoin className="w-5 h-5" />
                                                <div className="text-left">
                                                    <div className="font-medium">Crypto</div>
                                                    <div className="text-xs text-neutral-500">~30 minutes</div>
                                                </div>
                                            </button>
                                        </div>
                                    </div>

                                    {depositMethod === 'crypto' && (
                                        <div className="bg-neutral-950 p-4 rounded-xl border border-neutral-800 text-center">
                                            <div className="w-32 h-32 bg-white rounded-lg mx-auto mb-3 flex items-center justify-center">
                                                <QrCode className="w-24 h-24 text-black" />
                                            </div>
                                            <div className="text-xs text-neutral-500 mb-2">Send {selectedCurrency} to:</div>
                                            <div className="font-mono text-sm bg-neutral-900 p-2 rounded break-all">
                                                0x1234...5678
                                            </div>
                                        </div>
                                    )}

                                    {/* Amount Input */}
                                    <div>
                                        <label className="block text-sm font-medium text-neutral-400 mb-2">Amount</label>
                                        <div className="relative">
                                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500">$</span>
                                            <input
                                                type="number"
                                                value={amount}
                                                onChange={e => setAmount(e.target.value)}
                                                placeholder="0.00"
                                                className="w-full bg-neutral-950 border border-neutral-800 rounded-xl py-4 pl-8 pr-4 text-xl font-mono focus:outline-hidden focus:border-emerald-500"
                                            />
                                        </div>
                                        <div className="flex gap-2 mt-2">
                                            {[100, 500, 1000, 5000].map(preset => (
                                                <button
                                                    key={preset}
                                                    onClick={() => setAmount(preset.toString())}
                                                    className="flex-1 py-2 bg-neutral-800 rounded-lg text-sm font-mono hover:bg-neutral-700 transition-colors"
                                                >
                                                    ${preset}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <button
                                        onClick={handleDeposit}
                                        disabled={processing || !amount || parseFloat(amount) <= 0}
                                        className="w-full py-4 bg-emerald-500 text-black font-bold rounded-xl hover:bg-emerald-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {processing ? 'Processing...' : `Deposit ${selectedCurrency}`}
                                    </button>
                                </div>
                            )}
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* WITHDRAW MODAL */}
            <AnimatePresence>
                {modal === 'withdraw' && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-xs p-4">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="bg-neutral-900 border border-neutral-800 rounded-2xl w-full max-w-lg overflow-hidden"
                        >
                            <div className="p-6 border-b border-neutral-800 flex justify-between items-center">
                                <h2 className="text-xl font-bold">Withdraw Funds</h2>
                                <button onClick={() => setModal(null)} className="p-2 hover:bg-neutral-800 rounded-lg">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            {success ? (
                                <div className="p-12 text-center">
                                    <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <Check className="w-8 h-8 text-emerald-500" />
                                    </div>
                                    <h3 className="text-xl font-bold mb-2">Withdrawal Initiated!</h3>
                                    <p className="text-neutral-400">Your request is being processed</p>
                                </div>
                            ) : (
                                <div className="p-6 space-y-6">
                                    {/* Currency Selection */}
                                    <div>
                                        <label className="block text-sm font-medium text-neutral-400 mb-3">Select Currency</label>
                                        <div className="grid grid-cols-3 gap-2">
                                            {currencies.map(c => (
                                                <button
                                                    key={c.id}
                                                    onClick={() => setSelectedCurrency(c.id)}
                                                    className={`p-3 rounded-xl border text-center transition-all ${selectedCurrency === c.id
                                                        ? 'bg-white/10 border-white/20 text-white'
                                                        : 'border-neutral-800 text-neutral-500 hover:border-neutral-700'
                                                        }`}
                                                >
                                                    <div className="font-mono font-bold">{c.id}</div>
                                                    <div className="text-xs text-neutral-500">${wallet.balances[c.id].toLocaleString()}</div>
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Amount Input */}
                                    <div>
                                        <div className="flex justify-between items-center mb-2">
                                            <label className="text-sm font-medium text-neutral-400">Amount</label>
                                            <button
                                                onClick={() => setAmount(wallet.balances[selectedCurrency].toString())}
                                                className="text-xs text-emerald-500 hover:underline"
                                            >
                                                Max: ${wallet.balances[selectedCurrency].toLocaleString()}
                                            </button>
                                        </div>
                                        <div className="relative">
                                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500">$</span>
                                            <input
                                                type="number"
                                                value={amount}
                                                onChange={e => setAmount(e.target.value)}
                                                placeholder="0.00"
                                                max={wallet.balances[selectedCurrency]}
                                                className="w-full bg-neutral-950 border border-neutral-800 rounded-xl py-4 pl-8 pr-4 text-xl font-mono focus:outline-hidden focus:border-emerald-500"
                                            />
                                        </div>
                                    </div>

                                    {/* Fee Info */}
                                    <div className="bg-neutral-950 p-4 rounded-xl border border-neutral-800 space-y-2">
                                        <div className="flex justify-between text-sm">
                                            <span className="text-neutral-500">Amount</span>
                                            <span className="font-mono">${amount || '0.00'}</span>
                                        </div>
                                        <div className="flex justify-between text-sm">
                                            <span className="text-neutral-500">Network Fee</span>
                                            <span className="font-mono text-emerald-500">$0.00</span>
                                        </div>
                                        <div className="border-t border-neutral-800 pt-2 flex justify-between text-sm font-bold">
                                            <span>You'll Receive</span>
                                            <span className="font-mono">${amount || '0.00'}</span>
                                        </div>
                                    </div>

                                    {parseFloat(amount) > wallet.balances[selectedCurrency] && (
                                        <div className="flex items-center gap-2 text-red-500 text-sm">
                                            <AlertCircle className="w-4 h-4" />
                                            <span>Insufficient balance</span>
                                        </div>
                                    )}

                                    <button
                                        onClick={handleWithdraw}
                                        disabled={processing || !amount || parseFloat(amount) <= 0 || parseFloat(amount) > wallet.balances[selectedCurrency]}
                                        className="w-full py-4 bg-white text-black font-bold rounded-xl hover:bg-neutral-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {processing ? 'Processing...' : 'Confirm Withdrawal'}
                                    </button>
                                </div>
                            )}
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
}

// Transaction Row Component
function TransactionRow({ transaction }: { transaction: Transaction }) {
    const getTypeStyles = (type: string) => {
        switch (type) {
            case 'deposit':
                return { icon: ArrowDownLeft, color: 'text-emerald-500', bg: 'bg-emerald-500/10' };
            case 'withdrawal':
                return { icon: ArrowUpRight, color: 'text-red-500', bg: 'bg-red-500/10' };
            case 'invest':
                return { icon: ArrowUpRight, color: 'text-blue-500', bg: 'bg-blue-500/10' };
            case 'distribution':
                return { icon: ArrowDownLeft, color: 'text-purple-500', bg: 'bg-purple-500/10' };
            default:
                return { icon: Clock, color: 'text-neutral-500', bg: 'bg-neutral-500/10' };
        }
    };

    const styles = getTypeStyles(transaction.type);
    const Icon = styles.icon;

    return (
        <div className="flex items-center justify-between p-4 hover:bg-neutral-900/50 transition-colors">
            <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-xl ${styles.bg} flex items-center justify-center ${styles.color}`}>
                    <Icon className="w-5 h-5" />
                </div>
                <div>
                    <div className="font-medium capitalize">{transaction.type}</div>
                    <div className="text-sm text-neutral-500">
                        {transaction.description || transaction.assetName || '-'}
                    </div>
                </div>
            </div>
            <div className="text-right">
                <div className={`font-mono font-medium ${['deposit', 'distribution'].includes(transaction.type) ? 'text-emerald-500' : ''
                    }`}>
                    {['deposit', 'distribution'].includes(transaction.type) ? '+' : '-'}${transaction.amount.toLocaleString()}
                </div>
                <div className="text-xs text-neutral-500">
                    {new Date(transaction.timestamp).toLocaleDateString()} • {new Date(transaction.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
            </div>
            <div className="ml-4">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${transaction.status === 'completed'
                    ? 'bg-emerald-500/10 text-emerald-500'
                    : transaction.status === 'pending'
                        ? 'bg-amber-500/10 text-amber-500'
                        : 'bg-red-500/10 text-red-500'
                    }`}>
                    {transaction.status}
                </span>
            </div>
        </div>
    );
}
